import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { CartFeedbackService } from '../services/cart-feedback.service';
import { GuestSessionService } from '../services/guest-session.service';
import { I18nService } from '../services/i18n.service';
import { STOREFRONT_CLIENT_APP } from '../constants/storefront-storage.constants';
import { isStorefrontToken } from '../utils/jwt-token.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly apiOrigin = (environment.apiUrl ?? '').replace(/\/+$/, '');
  /** Prevents catalog/home/PDP skeletons from hanging forever on a stalled proxy/API. */
  private static readonly apiTimeoutMs = 25000;
  private static handlingSession401 = false;

  constructor(
    private authService: AuthService,
    private guestSession: GuestSessionService,
    private router: Router,
    private cartFeedback: CartFeedbackService,
    private i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.authService.getToken();
    const storefrontToken = isStorefrontToken(token) ? token : null;
    const isApiRequest = this.isStorefrontApiRequest(request.url);
    const shouldAttachToken = Boolean(storefrontToken) && isApiRequest;
    const hadStoredToken = Boolean(storefrontToken);
    const shouldAttachGuestSession =
      isApiRequest && !shouldAttachToken && isPlatformBrowser(this.platformId);

    const headers: Record<string, string> = {
      'X-Client-App': STOREFRONT_CLIENT_APP,
    };
    if (shouldAttachToken && storefrontToken) {
      headers['Authorization'] = `Bearer ${storefrontToken}`;
    }
    if (shouldAttachGuestSession) {
      headers['X-Guest-Session-Id'] = this.guestSession.getSessionId();
    }

    const outbound =
      Object.keys(headers).length > 0
        ? request.clone({ setHeaders: headers })
        : request;

    const handled$ = next.handle(outbound);
    const timed$ = isApiRequest
      ? handled$.pipe(timeout({ first: AuthInterceptor.apiTimeoutMs }))
      : handled$;

    return timed$.pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          this.maybeHandleUnauthorized(error, isApiRequest, hadStoredToken, request.url);
        }
        if (error instanceof TimeoutError) {
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 0,
                statusText: 'Timeout',
                url: request.url,
                error: { message: 'Request timed out' },
              })
          );
        }
        return throwError(() => error);
      })
    );
  }

  /** When the user had a token and the API returns 401, treat as expired/invalid session (not wrong-password login). */
  private maybeHandleUnauthorized(
    err: HttpErrorResponse,
    isApiRequest: boolean,
    hadStoredToken: boolean,
    requestUrl: string
  ): void {
    if (err.status !== 401 || !isApiRequest || !hadStoredToken || !isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.isAnonymousAccountPath(requestUrl)) {
      return;
    }
    if (AuthInterceptor.handlingSession401) {
      return;
    }
    AuthInterceptor.handlingSession401 = true;
    try {
      this.authService.logout();
      this.cartFeedback.showError(
        this.i18n.t('account.session_expired_message'),
        this.i18n.t('account.session_expired_title')
      );
      const alreadyOnLogin = /\/account\/login(?:\?|\/|$)/.test(this.router.url);
      if (!alreadyOnLogin) {
        const cleanReturn = this.router.url.split('#')[0] || '/';
        void this.router.navigate(['/account', 'login'], {
          queryParams: { returnUrl: cleanReturn },
          replaceUrl: true,
        });
      }
    } finally {
      setTimeout(() => {
        AuthInterceptor.handlingSession401 = false;
      }, 1600);
    }
  }

  private isStorefrontApiRequest(url: string): boolean {
    if (!url) {
      return false;
    }
    const origin = this.apiOrigin;
    if (origin && (url === origin || url.startsWith(`${origin}/`) || url.startsWith(`${origin}?`))) {
      return true;
    }
    try {
      const path = url.includes('://') ? new URL(url).pathname : url.split('?')[0];
      return path === '/api' || path.startsWith('/api/');
    } catch {
      return url.includes('/api/');
    }
  }

  private isAnonymousAccountPath(url: string): boolean {
    const u = url.split('?')[0].toLowerCase();
    return (
      u.includes('/account/login') ||
      u.includes('/account/userregister') ||
      u.includes('/account/change-password')
    );
  }
}
